<template>
  <div>
    <el-card shadow="never">
      <div slot="header" class="card-header">
        <span>首页快捷入口</span>
        <div class="header-actions">
          <el-button :loading="loading" @click="loadData">刷新</el-button>
          <el-button type="primary" @click="openCreate">新增入口</el-button>
        </div>
      </div>

      <el-alert
        v-if="loadError"
        class="state-block"
        title="快捷入口加载失败"
        type="error"
        :closable="false"
        show-icon
      >
        <template #default>
          <span>可以稍后重试，或先检查后端服务状态。</span>
          <el-button type="text" @click="reloadAll">重新加载</el-button>
        </template>
      </el-alert>

      <template v-else>
        <el-table v-if="loading || list.length" v-loading="loading" :data="list" border>
          <el-table-column label="图标" width="120">
            <template #default="{ row }">
              <div class="nav-icon">
                <img v-if="row.iconUrl" :src="row.iconUrl" alt="入口图标">
                <div v-else class="nav-icon__empty">暂无图标</div>
              </div>
            </template>
          </el-table-column>
          <el-table-column prop="title" label="标题" min-width="160" />
          <el-table-column label="跳转信息" min-width="240">
            <template #default="{ row }">
              <div>{{ jumpTypeText(row.jumpType) }}</div>
              <div class="sub-text">{{ jumpLabel(row) }}</div>
            </template>
          </el-table-column>
          <el-table-column prop="sort" label="排序" width="90" />
          <el-table-column label="状态" width="100">
            <template #default="{ row }">
              <el-tag :type="Number(row.status) === 1 ? 'success' : 'info'">
                {{ Number(row.status) === 1 ? '启用中' : '已停用' }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column label="操作" width="280">
            <template #default="{ row }">
              <el-button type="text" @click="openEdit(row.id)">编辑</el-button>
              <el-button
                v-if="Number(row.status) === 1"
                type="text"
                :loading="actionId === row.id"
                @click="down(row)"
              >停用</el-button>
              <el-button
                v-else
                type="text"
                :loading="actionId === row.id"
                @click="up(row)"
              >启用</el-button>
              <el-button type="text" :loading="actionId === row.id" @click="remove(row)">删除</el-button>
            </template>
          </el-table-column>
        </el-table>
        <el-empty v-else description="当前还没有首页快捷入口" />
      </template>
    </el-card>

    <el-dialog
      :title="form.id ? '编辑快捷入口' : '新增快捷入口'"
      :visible.sync="dialogVisible"
      width="760px"
      @closed="resetForm"
    >
      <el-form ref="formRef" :model="form" :rules="rules" label-width="100px">
        <el-form-item label="标题" prop="title">
          <el-input v-model.trim="form.title" placeholder="例如：春茶上新" />
        </el-form-item>
        <el-form-item label="图标" prop="iconUrl">
          <div class="upload-row">
            <div class="nav-icon nav-icon--large">
              <img v-if="form.iconUrl" :src="form.iconUrl" alt="图标预览">
              <div v-else class="nav-icon__empty">上传后预览</div>
            </div>
            <div class="upload-panel">
              <el-input v-model.trim="form.iconUrl" placeholder="请输入图标地址或直接上传" />
              <el-upload
                class="upload-inline"
                action="#"
                :show-file-list="false"
                :http-request="uploadIcon"
              >
                <el-button type="text" :loading="uploading">上传图标</el-button>
              </el-upload>
            </div>
          </div>
        </el-form-item>
        <el-form-item label="跳转类型" prop="jumpType">
          <el-select v-model="form.jumpType" placeholder="请选择跳转类型" @change="handleJumpTypeChange">
            <el-option :value="1" label="商品详情" />
            <el-option :value="2" label="分类页" />
            <el-option :value="3" label="不跳转" />
            <el-option :value="4" label="页面跳转" />
          </el-select>
        </el-form-item>
        <el-form-item v-if="Number(form.jumpType) === 1" label="跳转商品" prop="jumpValue">
          <el-select
            v-model="form.jumpValue"
            filterable
            remote
            clearable
            reserve-keyword
            placeholder="请选择商品"
            :remote-method="searchProducts"
            :loading="productLoading"
          >
            <el-option
              v-for="item in productOptions"
              :key="item.id"
              :label="item.name"
              :value="String(item.id)"
            />
          </el-select>
        </el-form-item>
        <el-form-item v-if="Number(form.jumpType) === 2" label="跳转分类" prop="jumpValue">
          <el-select
            v-model="form.jumpValue"
            filterable
            clearable
            placeholder="请选择分类"
          >
            <el-option
              v-for="item in categoryOptions"
              :key="item.id"
              :label="item.name"
              :value="String(item.id)"
            />
          </el-select>
        </el-form-item>
        <el-form-item v-if="Number(form.jumpType) === 4" label="目标页面" prop="jumpValue">
          <el-select
            v-model="form.jumpValue"
            filterable
            clearable
            placeholder="请选择页面"
          >
            <el-option
              v-for="item in pageOptions"
              :key="item.value"
              :label="item.label"
              :value="item.value"
            />
          </el-select>
          <div class="sub-text sub-text--field">
            已内置常用站内页面，后续如果新增页面，我们再把选项补进来。
          </div>
        </el-form-item>
        <el-form-item v-if="Number(form.jumpType) === 3" label="跳转值">
          <el-input value="当前不需要填写" disabled />
        </el-form-item>
        <el-form-item label="排序" prop="sort">
          <el-input-number v-model="form.sort" :min="0" :max="9999" />
        </el-form-item>
        <el-form-item label="状态" prop="status">
          <el-radio-group v-model="form.status">
            <el-radio :label="1">启用</el-radio>
            <el-radio :label="0">停用</el-radio>
          </el-radio-group>
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="saving" @click="submit">保存</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import {
  addHomeNav,
  deleteHomeNav,
  downHomeNav,
  getCategoryList,
  getHomeNavDetail,
  getHomeNavList,
  getProductDetail,
  getProductPage,
  upHomeNav,
  updateHomeNav,
  uploadAdminImage
} from '../../api/admin'

const createForm = () => ({
  id: null,
  title: '',
  iconUrl: '',
  jumpType: 3,
  jumpValue: '',
  sort: 1,
  status: 1
})

const pageOptions = [
  { label: '首页', value: '/pages/index/index' },
  { label: '分类页', value: '/pages/category/index' },
  { label: '购物车', value: '/pages/cart/index' },
  { label: '我的', value: '/pages/profile/index' },
  { label: '我的收藏', value: '/pages/favorite/index' },
  { label: '我的足迹', value: '/pages/footprint/index' },
  { label: '订单列表', value: '/pages/orders/index' },
  { label: '地址列表', value: '/pages/address/list' },
  { label: '积分商城', value: '/pages/point-mall/index' },
  { label: '团购拼团', value: '/pages/group-buy/index' }
]

export default {
  data() {
    return {
      list: [],
      loading: false,
      loadError: false,
      dialogVisible: false,
      form: createForm(),
      saving: false,
      uploading: false,
      actionId: null,
      productLoading: false,
      productOptions: [],
      productCache: {},
      categoryOptions: [],
      pageOptions,
      rules: {
        title: [{ required: true, message: '请输入入口标题', trigger: 'blur' }],
        iconUrl: [{ required: true, message: '请上传或填写入口图标', trigger: 'blur' }],
        jumpType: [{ required: true, message: '请选择跳转类型', trigger: 'change' }],
        jumpValue: [
          {
            validator: (rule, value, callback) => {
              if ([1, 2, 4].includes(Number(this.form.jumpType)) && !value) {
                callback(new Error('请选择跳转目标'))
                return
              }
              callback()
            },
            trigger: 'change'
          }
        ],
        sort: [{ required: true, message: '请输入排序值', trigger: 'change' }]
      }
    }
  },
  created() {
    this.reloadAll()
  },
  methods: {
    jumpTypeText(type) {
      const map = { 1: '商品详情', 2: '分类页', 3: '不跳转', 4: '页面跳转' }
      return map[type] || '未知类型'
    },
    jumpLabel(row) {
      if (Number(row.jumpType) === 1) {
        const product = this.productOptions.find(item => String(item.id) === String(row.jumpValue))
        return product ? `${product.name}（ID: ${row.jumpValue}）` : `商品ID：${row.jumpValue || '-'}`
      }
      if (Number(row.jumpType) === 2) {
        const category = this.categoryOptions.find(item => String(item.id) === String(row.jumpValue))
        return category ? `${category.name}（ID: ${row.jumpValue}）` : `分类ID：${row.jumpValue || '-'}`
      }
      if (Number(row.jumpType) === 4) {
        const page = this.pageOptions.find(item => item.value === row.jumpValue)
        return page ? `${page.label}（${page.value}）` : row.jumpValue || '未配置页面路径'
      }
      return '无跳转'
    },
    async reloadAll() {
      await Promise.allSettled([this.loadCategories(), this.searchProducts(''), this.loadData()])
    },
    async loadData() {
      this.loading = true
      this.loadError = false
      try {
        this.list = await getHomeNavList()
        await this.ensureProductOptionsByRows(this.list)
      } catch (error) {
        this.loadError = true
        this.$message.error(error?.message || '快捷入口加载失败')
      } finally {
        this.loading = false
      }
    },
    async loadCategories() {
      try {
        const categories = await getCategoryList()
        this.categoryOptions = categories || []
      } catch (error) {
        this.categoryOptions = []
        this.$message.error(error?.message || '分类加载失败')
      }
    },
    async searchProducts(keyword) {
      this.productLoading = true
      try {
        const productPage = await getProductPage({
          pageNum: 1,
          pageSize: 100,
          name: keyword || undefined
        })
        this.productOptions = this.mergeProductOptions((productPage && productPage.list) || [])
      } catch (error) {
        this.productOptions = []
        this.$message.error(error?.message || '商品加载失败')
      } finally {
        this.productLoading = false
      }
    },
    openCreate() {
      this.form = createForm()
      this.dialogVisible = true
    },
    async openEdit(id) {
      this.dialogVisible = true
      try {
        const detail = await getHomeNavDetail(id)
        if (detail.jumpValue && Number(detail.jumpType) === 1) {
          await this.ensureProductOption(detail.jumpValue)
        }
        this.form = {
          id: detail.id,
          title: detail.title || '',
          iconUrl: detail.iconUrl || '',
          jumpType: Number(detail.jumpType || 3),
          jumpValue: detail.jumpValue ? String(detail.jumpValue) : '',
          sort: Number(detail.sort || 0),
          status: Number(detail.status || 0)
        }
      } catch (error) {
        this.dialogVisible = false
        this.$message.error(error?.message || '快捷入口详情加载失败')
      }
    },
    mergeProductOptions(list = []) {
      const merged = [...list]
      Object.values(this.productCache).forEach(item => {
        if (!merged.find(current => String(current.id) === String(item.id))) {
          merged.push(item)
        }
      })
      return merged
    },
    async ensureProductOption(productId) {
      const id = String(productId || '')
      if (!id) return
      if (this.productOptions.find(item => String(item.id) === id)) return
      if (this.productCache[id]) {
        this.productOptions = this.mergeProductOptions(this.productOptions)
        return
      }
      try {
        const detail = await getProductDetail(id)
        const option = { id: detail.id, name: detail.name }
        this.$set(this.productCache, id, option)
        this.productOptions = this.mergeProductOptions(this.productOptions)
      } catch (error) {
        // ignore single product lookup failure to avoid blocking the page
      }
    },
    async ensureProductOptionsByRows(rows = []) {
      const ids = rows
        .filter(item => Number(item.jumpType) === 1 && item.jumpValue)
        .map(item => String(item.jumpValue))
        .filter((item, index, arr) => arr.indexOf(item) === index)
      for (const id of ids) {
        await this.ensureProductOption(id)
      }
    },
    handleJumpTypeChange() {
      this.form.jumpValue = ''
      if (this.$refs.formRef) {
        this.$refs.formRef.clearValidate('jumpValue')
      }
    },
    resetForm() {
      this.form = createForm()
      this.uploading = false
      this.saving = false
      if (this.$refs.formRef) {
        this.$refs.formRef.clearValidate()
      }
    },
    async uploadIcon(options) {
      try {
        this.uploading = true
        const result = await uploadAdminImage(options.file)
        this.form.iconUrl = result.url
      } catch (error) {
        this.$message.error(error?.message || '图标上传失败')
      } finally {
        this.uploading = false
      }
    },
    async submit() {
      try {
        if (Number(this.form.jumpType) === 3) {
          this.form.jumpValue = ''
        }
        await this.$refs.formRef.validate()
      } catch (error) {
        return
      }
      const payload = { ...this.form }
      this.saving = true
      try {
        if (payload.id) {
          await updateHomeNav(payload)
          this.$message.success('快捷入口已更新')
        } else {
          await addHomeNav(payload)
          this.$message.success('快捷入口已创建')
        }
        this.dialogVisible = false
        this.loadData()
      } catch (error) {
        this.$message.error(error?.message || '快捷入口保存失败')
      } finally {
        this.saving = false
      }
    },
    async up(row) {
      this.actionId = row.id
      try {
        await upHomeNav(row.id)
        this.$message.success('快捷入口已启用')
        this.loadData()
      } catch (error) {
        this.$message.error(error?.message || '快捷入口启用失败')
      } finally {
        this.actionId = null
      }
    },
    async down(row) {
      this.actionId = row.id
      try {
        await downHomeNav(row.id)
        this.$message.success('快捷入口已停用')
        this.loadData()
      } catch (error) {
        this.$message.error(error?.message || '快捷入口停用失败')
      } finally {
        this.actionId = null
      }
    },
    remove(row) {
      this.$confirm(`确认删除快捷入口“${row.title}”吗？`, '删除快捷入口', { type: 'warning' })
        .then(async () => {
          this.actionId = row.id
          try {
            await deleteHomeNav(row.id)
            this.$message.success('快捷入口已删除')
            this.loadData()
          } catch (error) {
            this.$message.error(error?.message || '快捷入口删除失败')
          } finally {
            this.actionId = null
          }
        })
        .catch(() => {})
    }
  }
}
</script>

<style scoped>
.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.header-actions {
  display: flex;
  gap: 12px;
}

.state-block {
  margin-bottom: 16px;
}

.nav-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 72px;
  height: 72px;
  border-radius: 18px;
  overflow: hidden;
  background: #f5f6f7;
  border: 1px solid #ebeef5;
}

.nav-icon img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.nav-icon__empty {
  color: #a0a6ad;
  font-size: 12px;
}

.nav-icon--large {
  width: 112px;
  height: 112px;
}

.sub-text {
  margin-top: 4px;
  color: #909399;
  font-size: 12px;
}

.sub-text--field {
  margin-top: 8px;
}

.upload-row {
  display: flex;
  gap: 16px;
  align-items: flex-start;
}

.upload-panel {
  flex: 1;
}

.upload-inline {
  margin-top: 8px;
}
</style>
